import {
  DropdownInputBare,
  type DropdownProps,
} from "@validereinc/common-components";
import {
  GetListRequestType,
  GetListResponseType,
  SortDirectionType,
  SortParamsType,
  type FilterObjectType,
} from "@validereinc/domain";
import React from "react";

/**
 * Props for the uncontrolled entity dropdown (uses DropdownInputBare, no form).
 * Use in FilterBuilder or other contexts where value is controlled by parent state.
 */
export type EntityDropdownInputBareProps<T> = Omit<
  DropdownProps,
  "onFetchData" | "value" | "onChange"
> & {
  /** Current value; controlled by parent (e.g. FilterBuilder filter state). */
  value: string | string[];
  /** Callback when selection changes. */
  onChange: (newValue: string | string[]) => void;
  fetchEntityList: (
    props: GetListRequestType
  ) => Promise<GetListResponseType<T>>;
  sort?: SortParamsType;
  fetchFilters?: Record<
    string,
    FilterObjectType<string | string[] | number | boolean>
  >;
  label?: string;
  isLabelShown?: boolean;
};

/**
 * Uncontrolled variant of EntityDropdownInput that uses DropdownInputBare.
 * Does not register with react-hook-form; use when value is controlled by parent (e.g. FilterBuilder).
 */
export const EntityDropdownInputBare = <T,>({
  value,
  onChange,
  isMulti = true,
  isFluid = false,
  isInline = false,
  isLabelShown = true,
  isDisabled,
  fetchEntityList,
  sort = { sortBy: "created_at", sortDirection: "desc" },
  fetchFilters = {},
  label,
  placeholder = "",
  isClearable = true,
  ...restProps
}: EntityDropdownInputBareProps<T>) => {
  const onFetchData = async ({
    value: selectedValue,
    pageSize = 25,
    page = 1,
    sortBy = sort.sortBy,
    sortDirection = sort.sortDirection,
    searchTerm,
  }: {
    value: string[];
    pageSize?: number;
    page?: number;
    sortBy?: string;
    sortDirection?: SortDirectionType;
    searchTerm?: string;
  }) => {
    const filters = {
      ...(searchTerm ? { name: searchTerm } : {}),
      ...(selectedValue?.length > 0 ? { id: selectedValue } : {}),
    };

    const { data } = await fetchEntityList({
      pageSize,
      page,
      sortBy,
      sortDirection,
      filters: { ...filters, ...fetchFilters },
    });

    return data;
  };

  return (
    <DropdownInputBare
      label={label}
      value={value}
      onChange={onChange}
      // @ts-expect-error TODO: onFetchData typing is weird
      onFetchData={onFetchData}
      labelKey="name"
      valueKey="id"
      isMulti={isMulti}
      isFluid={isFluid}
      isInline={isInline}
      isLabelShown={isLabelShown}
      isDisabled={isDisabled}
      isClearable={isClearable}
      placeholder={placeholder}
      {...restProps}
    />
  );
};
